import React, { useState, useEffect } from 'react';
import { FaWhatsapp, FaPaperPlane, FaPlus, FaTrash, FaEdit, FaCheckCircle, FaChartBar, FaSpinner } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import { useSubscription } from '../../hooks/useSubscription';

/**
 * WhatsApp Polls Component
 * 
 * Allows schools to create, manage, and send poll questions via WhatsApp
 * to collect feedback from contacts
 */
const WhatsAppPolls = () => {
  const { showToast } = useToast();
  const { hasTier } = useSubscription();
  
  // Check if user has access
  const hasAccess = hasTier(['elevate']);
  
  // States
  const [loading, setLoading] = useState(true);
  const [sendingPoll, setSendingPoll] = useState(false);
  const [polls, setPolls] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedPoll, setSelectedPoll] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // New poll form data
  const [newPoll, setNewPoll] = useState({
    title: '',
    question: '',
    options: ['', ''],
    footer: 'Select an option by clicking a button'
  });
  
  // Load polls on mount
  useEffect(() => {
    if (hasAccess) {
      fetchPolls();
      fetchContacts();
      fetchGroups();
    }
  }, [hasAccess]);
  
  // Fetch polls from API
  const fetchPolls = async () => {
    try {
      setLoading(true);
      const response = await api.get('/schools/whatsapp/polls');
      if (response.data && response.data.success) {
        setPolls(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching polls:', error);
      showToast('Failed to load WhatsApp polls', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch contacts from API
  const fetchContacts = async () => {
    try {
      const response = await api.get('/schools/whatsapp/contacts');
      if (response.data && response.data.success) {
        setContacts(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };
  
  // Fetch groups from API
  const fetchGroups = async () => {
    try {
      const response = await api.get('/schools/whatsapp/groups');
      if (response.data && response.data.success) {
        setGroups(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPoll(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle option input change
  const handleOptionChange = (index, value) => {
    setNewPoll(prev => {
      const newOptions = [...prev.options];
      newOptions[index] = value;
      return {
        ...prev,
        options: newOptions
      };
    });
  };
  
  // Add new option
  const addOption = () => {
    if (newPoll.options.length < 10) {
      setNewPoll(prev => ({
        ...prev,
        options: [...prev.options, '']
      }));
    } else {
      showToast('Maximum 10 options allowed for a poll', 'warning');
    }
  };
  
  // Remove option
  const removeOption = (index) => {
    if (newPoll.options.length <= 2) {
      showToast('At least 2 options are required', 'warning');
      return;
    }
    
    setNewPoll(prev => {
      const newOptions = [...prev.options];
      newOptions.splice(index, 1);
      return {
        ...prev,
        options: newOptions
      };
    });
  };
  
  // Toggle contact selection
  const toggleContact = (contactId) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };
  
  // Toggle group selection
  const toggleGroup = (groupId) => {
    setSelectedGroups(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };
  
  // Reset form data
  const resetForm = () => {
    setNewPoll({
      title: '',
      question: '',
      options: ['', ''],
      footer: 'Select an option by clicking a button'
    });
    setSelectedContacts([]);
    setSelectedGroups([]);
  };
  
  // Create poll
  const createPoll = async () => {
    // Validation
    if (!newPoll.title.trim()) {
      showToast('Poll title is required', 'error');
      return;
    }
    
    if (!newPoll.question.trim()) {
      showToast('Poll question is required', 'error');
      return;
    }
    
    // Validate options
    const validOptions = newPoll.options.filter(opt => opt.trim());
    if (validOptions.length < 2) {
      showToast('At least 2 valid options are required', 'error');
      return;
    }
    
    // Validate recipients
    if (selectedContacts.length === 0 && selectedGroups.length === 0) {
      showToast('Select at least one recipient (contact or group)', 'error');
      return;
    }
    
    try {
      setLoading(true);
      
      // Format recipients
      const recipients = [
        ...selectedContacts.map(id => ({ type: 'contact', id })),
        ...selectedGroups.map(id => ({ type: 'group', id }))
      ];
      
      // Create poll request
      const response = await api.post('/schools/whatsapp/polls', {
        title: newPoll.title.trim(),
        question: newPoll.question.trim(),
        options: validOptions,
        footer: newPoll.footer.trim() || 'Select an option by clicking a button',
        recipients
      });
      
      if (response.data && response.data.success) {
        showToast('Poll created successfully', 'success');
        
        // Close modal and refresh polls
        setShowCreateModal(false);
        resetForm();
        fetchPolls();
      }
    } catch (error) {
      console.error('Error creating poll:', error);
      showToast(error.response?.data?.error || 'Failed to create poll', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Send poll
  const sendPoll = async (pollId) => {
    try {
      setSendingPoll(pollId);
      
      const response = await api.post(`/schools/whatsapp/polls/${pollId}/send`);
      
      if (response.data && response.data.success) {
        const { messagesSent, messagesFailed } = response.data.data;
        
        if (messagesFailed > 0) {
          showToast(`Poll sent to ${messagesSent} recipients, failed for ${messagesFailed} recipients`, 'warning');
        } else {
          showToast(`Poll sent successfully to ${messagesSent} recipients`, 'success');
        }
        
        // Refresh polls
        fetchPolls();
      }
    } catch (error) {
      console.error('Error sending poll:', error);
      showToast(error.response?.data?.error || 'Failed to send poll', 'error');
    } finally {
      setSendingPoll(false);
    }
  };
  
  // View poll results
  const viewResults = async (poll) => {
    setSelectedPoll(poll);
    setShowResultsModal(true);
  };
  
  // Filter polls based on search query
  const filteredPolls = polls.filter(poll => 
    poll.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
    poll.question.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not sent yet';
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  // Calculate poll statistics
  const calculateStats = (poll) => {
    if (!poll || !poll.responses || poll.responses.length === 0) {
      return [];
    }
    
    // Count responses for each option
    const stats = {};
    poll.options.forEach(option => {
      stats[option.id] = {
        id: option.id,
        text: option.text,
        count: 0,
        percentage: 0
      };
    });
    
    // Count responses
    poll.responses.forEach(response => {
      if (stats[response.selectedOption]) {
        stats[response.selectedOption].count += 1;
      }
    });
    
    // Calculate percentages
    const totalResponses = poll.responses.length;
    Object.keys(stats).forEach(key => {
      stats[key].percentage = Math.round((stats[key].count / totalResponses) * 100);
    });
    
    return Object.values(stats);
  };
  
  // Render upgrade message for schools without access
  if (!hasAccess) {
    return (
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 text-center">
        <FaWhatsapp className="text-green-500 text-5xl mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-blue-800 mb-3">
          Upgrade to Elevate Plan for WhatsApp Polls
        </h2>
        <p className="text-blue-700 mb-6 max-w-lg mx-auto">
          The WhatsApp Polls feature allows schools to create interactive polls and feedback forms
          for parents and staff. Upgrade to our Elevate plan to unlock this functionality.
        </p>
        <a href="/school/subscription" className="btn-primary">
          Upgrade Now
        </a>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaWhatsapp className="mr-2 text-green-500" /> WhatsApp Polls
          </h1>
          <p className="text-gray-600 mt-1">
            Create and send interactive polls to your contacts
          </p>
        </div>
        
        <div className="mt-4 md:mt-0">
          <button 
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center"
          >
            <FaPlus className="mr-2" /> Create New Poll
          </button>
        </div>
      </div>
      
      {/* Search bar */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search polls..."
          className="form-input w-full md:w-1/3"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      
      {/* Polls list */}
      {loading ? (
        <div className="text-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : filteredPolls.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No polls found</p>
          <p className="text-gray-400 text-sm mt-2">
            Create your first poll by clicking the "Create New Poll" button
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPolls.map(poll => (
            <div key={poll._id} className="bg-white rounded-lg shadow-soft overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-semibold text-lg">{poll.title}</h3>
                <p className="text-gray-500 text-sm mt-1">
                  Created: {formatDate(poll.createdAt)}
                </p>
              </div>
              
              <div className="p-4">
                <p className="font-medium">{poll.question}</p>
                
                <div className="mt-3">
                  <p className="text-sm text-gray-500 mb-2">Options:</p>
                  <ul className="text-sm space-y-1">
                    {poll.options.map((option, index) => (
                      <li key={option.id} className="bg-gray-50 p-2 rounded">
                        {index + 1}. {option.text}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">
                      {poll.responses?.length || 0} responses
                    </span>
                    <span className={`text-sm px-2 py-1 rounded ${
                      poll.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {poll.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <div className="mt-1 text-sm text-gray-500">
                    {poll.sentAt ? (
                      <span>Last sent: {formatDate(poll.sentAt)}</span>
                    ) : (
                      <span>Not sent yet</span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-gray-50 flex justify-between">
                <button
                  onClick={() => viewResults(poll)}
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                >
                  <FaChartBar className="mr-1" /> View Results
                </button>
                
                <button
                  onClick={() => sendPoll(poll._id)}
                  disabled={sendingPoll === poll._id || !poll.isActive}
                  className={`text-green-600 hover:text-green-800 text-sm flex items-center ${
                    !poll.isActive ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {sendingPoll === poll._id ? (
                    <>
                      <FaSpinner className="mr-1 animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane className="mr-1" /> Send Poll
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Create Poll Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="Create New WhatsApp Poll"
      >
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Poll Title (Header) <span className="text-red-500">*</span></label>
            <input
              type="text"
              name="title"
              className="form-input w-full"
              value={newPoll.title}
              onChange={handleInputChange}
              placeholder="E.g., [School Name] Feedback Capture"
            />
            <p className="text-xs text-gray-500 mt-1">
              This will appear as the header of the WhatsApp message
            </p>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Poll Question <span className="text-red-500">*</span></label>
            <textarea
              name="question"
              className="form-textarea w-full"
              value={newPoll.question}
              onChange={handleInputChange}
              placeholder="E.g., How would you rate the school's communication with parents?"
              rows="3"
            />
          </div>
          
          <div className="mb-4">
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium">Poll Options <span className="text-red-500">*</span></label>
              <button
                type="button"
                onClick={addOption}
                className="text-primary hover:text-primary-dark text-sm flex items-center"
              >
                <FaPlus className="mr-1" /> Add Option
              </button>
            </div>
            
            <p className="text-xs text-gray-500 mb-2">
              Add up to 10 options. Note: WhatsApp will show 3 buttons initially, with a "See all options" button for the rest.
            </p>
            
            <div className="space-y-2">
              {newPoll.options.map((option, index) => (
                <div key={index} className="flex">
                  <input
                    type="text"
                    className="form-input flex-grow"
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeOption(index)}
                    className="ml-2 text-red-500 hover:text-red-700"
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Footer Text</label>
            <input
              type="text"
              name="footer"
              className="form-input w-full"
              value={newPoll.footer}
              onChange={handleInputChange}
              placeholder="Select an option by clicking a button"
            />
            <p className="text-xs text-gray-500 mt-1">
              This will appear at the bottom of the poll message
            </p>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Recipients <span className="text-red-500">*</span></label>
            <p className="text-xs text-gray-500 mb-2">
              Select contacts and/or groups to receive this poll
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium mb-2">Contacts</h4>
                <div className="max-h-40 overflow-y-auto">
                  {contacts.length === 0 ? (
                    <p className="text-gray-500 text-sm">No contacts found</p>
                  ) : (
                    <div className="space-y-1">
                      {contacts.map(contact => (
                        <div 
                          key={contact._id}
                          className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                          onClick={() => toggleContact(contact._id)}
                        >
                          <input
                            type="checkbox"
                            className="form-checkbox mr-2"
                            checked={selectedContacts.includes(contact._id)}
                            onChange={() => {}}
                          />
                          <span>{contact.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium mb-2">Groups</h4>
                <div className="max-h-40 overflow-y-auto">
                  {groups.length === 0 ? (
                    <p className="text-gray-500 text-sm">No groups found</p>
                  ) : (
                    <div className="space-y-1">
                      {groups.map(group => (
                        <div 
                          key={group._id}
                          className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                          onClick={() => toggleGroup(group._id)}
                        >
                          <input
                            type="checkbox"
                            className="form-checkbox mr-2"
                            checked={selectedGroups.includes(group._id)}
                            onChange={() => {}}
                          />
                          <span>{group.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mt-2 text-sm">
              Selected: {selectedContacts.length} contacts, {selectedGroups.length} groups
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={createPoll}
              className="btn-primary flex items-center"
              disabled={loading}
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" /> Creating...
                </>
              ) : (
                <>
                  <FaPlus className="mr-2" /> Create Poll
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
      
      {/* Poll Results Modal */}
      <Modal
        isOpen={showResultsModal}
        onClose={() => {
          setShowResultsModal(false);
          setSelectedPoll(null);
        }}
        title={selectedPoll ? `Poll Results: ${selectedPoll.title}` : 'Poll Results'}
      >
        {selectedPoll && (
          <div className="p-6">
            <div className="mb-4">
              <h3 className="font-medium text-lg mb-1">{selectedPoll.question}</h3>
              <p className="text-sm text-gray-500">
                Total Responses: {selectedPoll.responses?.length || 0}
              </p>
            </div>
            
            {!selectedPoll.responses || selectedPoll.responses.length === 0 ? (
              <div className="py-8 text-center bg-gray-50 rounded-lg">
                <p className="text-gray-500">No responses yet</p>
              </div>
            ) : (
              <div>
                <div className="mb-6">
                  {calculateStats(selectedPoll).map(stat => (
                    <div key={stat.id} className="mb-3">
                      <div className="flex justify-between mb-1">
                        <span>{stat.text}</span>
                        <span className="font-medium">{stat.count} ({stat.percentage}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className="bg-primary h-2.5 rounded-full" 
                          style={{ width: `${stat.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <h4 className="font-medium mb-2">Individual Responses</h4>
                <div className="max-h-60 overflow-y-auto border rounded-md">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Response
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Time
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedPoll.responses.map((response, index) => {
                        // Find the option text
                        const option = selectedPoll.options.find(opt => opt.id === response.selectedOption);
                        const optionText = option ? option.text : 'Unknown option';
                        
                        return (
                          <tr key={index}>
                            <td className="px-4 py-2 whitespace-nowrap">
                              {response.contact ? (
                                <span>Contact ID: {response.contact}</span>
                              ) : (
                                <span>{response.phoneNumber}</span>
                              )}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              {optionText}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(response.respondedAt)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowResultsModal(false);
                  setSelectedPoll(null);
                }}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WhatsAppPolls; 